import React, { useState, useEffect, useRef } from "react";
import {
  Plus,
  MessageSquare,
  Send,
  Edit,
  SaveIcon,
  TrashIcon,
  X,
  Eye,
} from "lucide-react";
import { noteService } from "@/services/noteService";
import { noteTemplateService } from "@/services/noteTemplateService";
import candidateService from "@/services/candidateService";
import Dropdown from "@/components/ui/Dropdown";
import DialogBox from "@/components/Common/DialogBox";
import Loader from "@/components/Loader";

import useAuth from "@/hooks/stores/useAuth";
import { autoResizeTextarea } from "@/utils/helpers";

// Separate dialog component
const NoteDialog = ({
  isOpen,
  mode,
  noteValue,
  onValueChange,
  noteCategory,
  onCategoryChange,
  noteTemplate,
  onTemplateChange,
  noteTemplates,
  onSave,
  onClose,
  noteCategories,
  sendEmail,
  onSendEmailChange,
}) => {
  const [isMounted, setIsMounted] = useState(false);
  const textareaRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setIsMounted(true);
      // Instead of disabling all scrolling, just make the drawer overlay fixed
      document.body.style.paddingRight = "17px"; // Prevent layout shift when scrollbar disappears
    } else {
      setIsMounted(false);
      // Clean up when drawer closes
      document.body.style.paddingRight = "";
    }
  }, [isOpen]);

  useEffect(() => {
    if (textareaRef.current && isOpen) {
      autoResizeTextarea(textareaRef.current, 500);
    }
  }, [noteValue, isOpen]);

  if (!isOpen) return null;

  const isAddMode = mode === "add";

  return (
    <>
      {/* Semi-transparent overlay that doesn't handle clicks */}
      <div className="fixed inset-0 z-[98]  pointer-events-none" />
      <div className="fixed inset-0 z-[99] flex justify-end pointer-events-none">
        <div
          className={`bg-card rounded-lg shadow-xl p-6 w-[90%] max-w-md relative ml-4 transform transition-transform duration-300 ease-out pointer-events-auto ${isMounted ? "translate-x-0" : "translate-x-full"
            }`}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-foreground">
              {isAddMode ? "Add Note" : "Edit Note"}
            </h3>
            <button
              onClick={onClose}
              className="text-muted-foreground hover:text-foreground"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* note templates */}
          <Dropdown
            className="w-full mb-2"
            options={noteTemplates}
            value={
              noteTemplates.find(
                (template) => String(template.value) === String(noteTemplate),
              ) || null
            }
            placeholder="Select Template"
            onChange={(selectedOption) =>
              onTemplateChange(selectedOption?.value || "")
            }
            searchable={true}
            triggerClassName="!bg-card !border !border-border hover:!border-primary-dark"
          />
          <textarea
            ref={textareaRef}
            value={noteValue}
            onChange={(e) => {
              onValueChange(e.target.value);
              autoResizeTextarea(e.target, 900);
            }}
            placeholder="Add your note here..."
            className="w-full p-3 rounded-lg border border-border focus:outline-none focus:border-primary-dark min-h-[150px] max-h-[50vh] resize-none"
          />

          <Dropdown
            className="w-full mt-3"
            options={noteCategories}
            value={
              noteCategories.find(
                (category) => String(category.value) === String(noteCategory),
              ) || null
            }
            placeholder="Select Category"
            onChange={(selectedOption) =>
              onCategoryChange(selectedOption?.value || "")
            }
            searchable={true}
            triggerClassName="!bg-card !border !border-border hover:!border-primary-dark"
          />



          <div className="mt-4 flex items-center">
            <button
              type="button"
              onClick={() => onSendEmailChange(!sendEmail)}
              aria-pressed={sendEmail}
              className={`relative inline-flex h-6 w-11 items-center rounded-full border border-border transition-colors ${sendEmail ? "bg-primary-dark border-transparent" : "bg-muted"}`}
            >
              <span
                className={`inline-block h-5 w-5 transform rounded-full bg-card border border-border shadow-sm transition-transform ${sendEmail ? "translate-x-5" : "translate-x-1"}`}
              />
            </button>
            <span className="ml-3 text-sm text-foreground">
              Send email notification
            </span>
          </div>

          <div className="flex justify-end mt-4 gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-foreground bg-muted rounded-lg hover:bg-muted"
            >
              Cancel
            </button>
            <button
              onClick={onSave}
              disabled={!noteValue.trim()}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-dark rounded-lg hover:scale-105 disabled:opacity-80 disabled:hover:bg-primary-dark"
            >
              {isAddMode ? "Add" : "Save"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

function CandidateNotes({
  applicationId,
  candidateId = null,
  notes,
  onAddNote = null,
  onEditNote = null,
  hideHeader = false,
  hideAddButton = false,
  readOnly = false,
}) {
  const [newNote, setNewNote] = useState("");
  const [isAddingNote, setIsAddingNote] = useState(false);
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editedNote, setEditedNote] = useState("");
  const [candidateNotes, setCandiateNotes] = useState(notes || []);
  const [showNoteDialog, setShowNoteDialog] = useState(false);
  const [showViewNotesDialog, setShowViewNotesDialog] = useState(false);
  const [allApplicationsNotes, setAllApplicationsNotes] = useState([]);
  const [isLoadingAllNotes, setIsLoadingAllNotes] = useState(false);
  const [dialogMode, setDialogMode] = useState("add"); // "add" or "edit"
  const [currentNoteValue, setCurrentNoteValue] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [noteCategories, setNoteCategories] = useState([]);
  const [allCategories, setAllCategories] = useState([]);
  const [sendEmail, setSendEmail] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [noteTemplates, setNoteTemplates] = useState([]);

  const { userID } = useAuth();

  // Update candidateNotes from props safely:
  // - Initialize on first render
  // - Apply only non-destructive updates (don't shrink the list),
  //   to avoid transient parent states hiding existing notes
  const hasInitializedFromPropsRef = useRef(false);
  const prevApplicationIdRef = useRef(applicationId);

  useEffect(() => {
    const incomingNotes = Array.isArray(notes) ? notes : [];
    // if (prevApplicationIdRef.current !== applicationId) {
    //   setCandiateNotes(incomingNotes);
    //   prevApplicationIdRef.current = applicationId;
    //   hasInitializedFromPropsRef.current = true;
    //   return;
    // }

    if (!hasInitializedFromPropsRef.current) {
      setCandiateNotes(incomingNotes);
      hasInitializedFromPropsRef.current = true;
      return;
    }

    // If incoming has more or equal items, or if local is empty, sync from props
    if (
      incomingNotes.length >= (candidateNotes?.length || 0) ||
      (candidateNotes?.length || 0) === 0
    ) {
      setCandiateNotes(incomingNotes);
    }
    // Otherwise, keep local state (e.g., just appended a note locally)
  }, [notes, applicationId]);

  // Fetch note categories on component mount
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await noteService.fetchNotes();
        if (response && Array.isArray(response.categories)) {
          setAllCategories(response.categories);
          const categories = response.categories.map((category) => ({
            value: category.id,
            label: category.apptkr_title,
          }));
          setNoteCategories(categories);
        } else {
          console.error("Unexpected response structure:", response);
        }
      } catch (error) {
        console.error("Failed to fetch note categories:", error);
      }
    };

    fetchCategories();
  }, []);

  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const response = await noteTemplateService.getAllTemplates();
        const list = Array.isArray(response?.data)
          ? response.data
          : Array.isArray(response?.templates)
            ? response.templates
            : Array.isArray(response)
              ? response
              : [];

        setNoteTemplates(
          list.map((template) => ({
            value: String(template.id),
            label: template.name ?? template.title ?? "Untitled Template",
            body: template.body ?? template.content ?? "",
          })),
        );
      } catch (error) {
        console.error("Failed to fetch note templates:", error);
        setNoteTemplates([]);
      }
    };

    fetchTemplates();
  }, []);

  // Set initial value when dialog mode changes
  useEffect(() => {
    if (dialogMode === "add") {
      setCurrentNoteValue(newNote);
    } else {
      setCurrentNoteValue(editedNote);
    }
  }, [dialogMode, showNoteDialog]);

  const openAddNoteDialog = () => {
    setNewNote("");
    setCurrentNoteValue("");
    setSelectedCategory("");
    setSelectedTemplate("");
    setSendEmail(false);
    setDialogMode("add");
    setShowNoteDialog(true);
  };

  const openViewNotesDialog = async () => {
    setShowViewNotesDialog(true);

    if (!candidateId) {
      // Fallback: show current application notes only
      setAllApplicationsNotes(
        candidateNotes?.length
          ? [{ vacancyTitle: "Current Application", notes: candidateNotes }]
          : [],
      );
      return;
    }

    setIsLoadingAllNotes(true);
    try {
      const response = await candidateService.getCandidateById(candidateId);
      const applications = response?.data?.applications || [];
      const appsWithNotes = applications
        .filter((app) => (app.application_notes?.length || 0) > 0)
        .map((app) => ({
          id: app.id,
          vacancyTitle: app.vacancy?.title || "Untitled Vacancy",
          notes: app.application_notes || [],
        }));
      setAllApplicationsNotes(appsWithNotes);
    } catch (error) {
      console.error("Failed to fetch all candidate notes:", error);
      setAllApplicationsNotes(
        candidateNotes?.length
          ? [{ vacancyTitle: "Current Application", notes: candidateNotes }]
          : [],
      );
    } finally {
      setIsLoadingAllNotes(false);
    }
  };

  const closeViewNotesDialog = () => {
    setShowViewNotesDialog(false);
  };

  const openEditNoteDialog = (note) => {
    setEditingNoteId(note.id);
    setEditedNote(note.comment);
    setCurrentNoteValue(note.comment);
    setSelectedCategory(note.note_category?.id || "");
    setSelectedTemplate("");
    setSendEmail(false); // Reset email option for edit mode
    setDialogMode("edit");
    setShowNoteDialog(true);
  };

  const closeNoteDialog = () => {
    setShowNoteDialog(false);
    if (dialogMode === "add") {
      setIsAddingNote(false);
    } else {
      setEditingNoteId(null);
    }
  };

  const handleAddNote = async () => {
    if (currentNoteValue.trim() && applicationId) {
      try {
        const { data: added_note } = await noteService.createNote(
          applicationId,
          {
            comment: currentNoteValue.trim(),
            note_category: selectedCategory,
            sendEmail: sendEmail,
          },
        );

        if (added_note && added_note[0]) {
          const note_category = allCategories.find(
            (category) => category.id == selectedCategory,
          );
          const newNote = { ...added_note[0], note_category };

          setCandiateNotes((prev) => [...prev, newNote]);

          // Call the parent's onAddNote if provided
          if (onAddNote) {
            onAddNote(applicationId, newNote);
          }

          setNewNote("");
          closeNoteDialog();
        }
      } catch (error) {
        console.error("Failed to add note:", error);
      }
    }
  };

  const handleEditNote = (note) => {
    openEditNoteDialog(note);
  };

  const handleDeleteNote = async (noteId) => {
    if (!applicationId) return;

    try {
      await noteService.deleteNote(applicationId, noteId);
      setCandiateNotes((prev) => prev.filter((note) => note.id !== noteId));
    } catch (error) {
      console.error("Failed to delete note:", error);
    }
  };

  const handleSaveEdit = async () => {
    if (currentNoteValue.trim() && applicationId && editingNoteId) {
      try {
        const note_category = allCategories.find(
          (category) => category.id == selectedCategory,
        );
        const updatedNote = {
          comment: currentNoteValue.trim(),
          note_category,
        };

        await noteService.updateNote(applicationId, editingNoteId, {
          comment: currentNoteValue.trim(),
          note_category: selectedCategory,
        });

        setCandiateNotes((prev) =>
          prev.map((note) =>
            note.id === editingNoteId ? { ...note, ...updatedNote } : note,
          ),
        );

        // Call the parent's onEditNote if provided
        if (onEditNote) {
          onEditNote(applicationId, updatedNote, editingNoteId);
        }

        closeNoteDialog();
      } catch (error) {
        console.error("Failed to update note:", error);
      }
    }
  };

  const handleSaveNote = () => {
    if (dialogMode === "add") {
      handleAddNote();
    } else {
      handleSaveEdit();
    }
  };

  const handleTemplateChange = (templateId) => {
    setSelectedTemplate(templateId);
    const selected = noteTemplates.find(
      (template) => template.value === String(templateId),
    );
    if (selected) {
      setCurrentNoteValue(selected.body || "");
    }
  };

  return (
    <div className="bg-card rounded-lg border border-border p-4">
      {!hideHeader && (
        <h3 className="text-lg font-semibold text-foreground flex items-center gap-2 mb-3">
          <MessageSquare className="w-5 h-5" />
          Recruiter Notes
        </h3>
      )}

      <div className="space-y-4 mb-4">
        {candidateNotes?.length > 0 ? (
          candidateNotes.map((note) => (
            <div key={note.id} className="bg-muted p-3 rounded-lg relative">
              <div className="mb-2">
                <p className="break-all text-wrap text-foreground whitespace-pre-wrap flex-1 pr-10">
                  {note.comment}
                </p>
                {!readOnly && note.created_by?.id == userID && (
                  <div className="absolute top-3 right-2 flex items-center">
                    <button
                      className="text-foreground hover:text-foreground mx-1"
                      onClick={() => handleEditNote(note)}
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      className="text-red-500 hover:text-foreground mx-1"
                      onClick={() => handleDeleteNote(note.id)}
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>

              <div className="flex items-center justify-between text-xs text-foreground capitalize">
                <span className="bg-secondary-soft border border-primary-dark px-2 py-1 rounded-lg">
                  {note?.note_category?.apptkr_title || "N/A"}
                </span>
                <div className="flex items-center flex-col">
                  <span>
                    {note?.created_by?.name || (
                      <span className="italic">Deleted User</span>
                    )}
                  </span>
                  <span>
                    {note?.created_at
                      ? new Date(note.created_at).toLocaleDateString()
                      : new Date().toLocaleDateString()}
                  </span>
                </div>
              </div>

              <div className="flex items-center justify-between text-sm text-foreground"></div>
            </div>
          ))
        ) : (
          <p className="text-muted-foreground text-sm italic">No notes added yet.</p>
        )}
      </div>

        {!hideAddButton && (
          <div className="flex gap-2">

<button
              onClick={openViewNotesDialog}
              className="flex-1 bg-primary-dark hover:opacity-90 text-white text-sm font-medium py-2 px-3 rounded-lg flex items-center justify-center gap-1.5 transition"
            >
              <Eye className="w-3.5 h-3.5" />
              View Notes
            </button>

            <button
              onClick={openAddNoteDialog}
              className="flex-1 bg-primary-dark hover:opacity-90 text-white text-sm font-medium py-2 px-3 rounded-lg flex items-center justify-center gap-1.5 transition"
            >
              <Plus className="w-3.5 h-3.5" />
              Add Note
            </button>
            
          </div>
        )}
      <NoteDialog
        isOpen={showNoteDialog}
        mode={dialogMode}
        noteValue={currentNoteValue}
        onValueChange={setCurrentNoteValue}
        noteCategory={selectedCategory}
        onCategoryChange={setSelectedCategory}
        noteTemplate={selectedTemplate}
        onTemplateChange={handleTemplateChange}
        noteTemplates={noteTemplates}
        onSave={handleSaveNote}
        onClose={closeNoteDialog}
        noteCategories={noteCategories}
        sendEmail={sendEmail}
        onSendEmailChange={setSendEmail}
      />

      <DialogBox
        isOpen={showViewNotesDialog}
        onClose={closeViewNotesDialog}
        title={
          <span className="text-xl font-semibold pl-4 text-foreground">
            All Application Notes
          </span>
        }
        showIcon={false}
        className="w-[60%] max-md:w-[95%]"
      >
        <div className="space-y-4 p-2">
          {isLoadingAllNotes ? (
            <div className="flex items-center justify-center min-h-[240px]">
              <Loader
                loadingText="Loading notes..."
                bgColor="transparent"
                className="w-10 h-10 text-[#6ab89a]"
                containerClassName="bg-transparent"
              />
            </div>
          ) : allApplicationsNotes.length > 0 ? (
            allApplicationsNotes.map((app) => (
              <div key={app.id || app.vacancyTitle} className="space-y-2">
                <h4 className="text-sm font-semibold text-foreground bg-muted px-3 py-1.5 rounded-md inline-block">
                  Notes for: {app.vacancyTitle}
                </h4>
                {app.notes.map((note) => (
                  <div key={note.id} className="bg-muted p-3 rounded-lg">
                    <p className="break-all text-wrap text-foreground whitespace-pre-wrap mb-2">
                      {note.comment}
                    </p>
                    <div className="flex items-center justify-between text-xs text-foreground capitalize">
                      <span className="bg-secondary-soft border border-primary-dark px-2 py-1 rounded-lg">
                        {note?.note_category?.apptkr_title || "N/A"}
                      </span>
                      <div className="flex items-center flex-col">
                        <span>
                          {note?.created_by?.name || (
                            <span className="italic">Deleted User</span>
                          )}
                        </span>
                        <span>
                          {note?.created_at
                            ? new Date(note.created_at).toLocaleDateString()
                            : new Date().toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ))
          ) : (
            <p className="text-muted-foreground text-sm italic">No notes added yet.</p>
          )}
        </div>
      </DialogBox>
    </div>
  );
}

export default CandidateNotes;
